import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { useEffect, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import FormFooter from "components/forms/FormFooter";
import TemplateUsers, { type TemplateUsersRef } from "../../TemplateUsers/TemplateUsers";
import { setSelected as setSelectedUsers } from "features/Accounts/state/slices/editAccountUsersSlice";
import { bindAction, type IObservable } from "interfaces";
import { SortingDirection } from "enums";
import { fetchAccountUsers } from "../../state/thunks/editAccountUsersThunk";
import { Columns, columnToParamMap } from "../../TemplateUsers/columnOptions";
import { useObserver } from "hooks/useObserver";
import { deleteUsers } from "../state/thunks/peopleThunk";

export type EditAccountUsersListOwnProps = {
  editAccountId: number;
  renderAddUsersButton: () => JSX.Element;
  isNavigationEnabled?: boolean;
  onAddUserModalObserver: IObservable<(onConfirm: () => void) => void>;
  onImportUsersModalObserver: IObservable<(onConfirm: () => void) => void>;
  onDeleteUsersObserver: IObservable<(onConfirm: () => void, ids: number[]) => void>;
  isReadOnly?: boolean;
} & PropsFromRedux;

const EditAccountUsersList = (props: EditAccountUsersListOwnProps) => {
  const { editAccountId, fetchAccountUsers, onImportUsersModalObserver } = props;
  const [isDirty, setIsDirty] = useState(false);
  const ref = useRef<TemplateUsersRef>(null);
  const [showUserUploadModal, setShowUsersFileUploadModal] = useState(false);

  const [subscribe] = useObserver(onImportUsersModalObserver);

  useEffect(() => {
    return subscribe(() => setShowUsersFileUploadModal(true));
  }, [subscribe]);

  const loadAccountUsers = (
    skip: number = 0,
    top: number = 10,
    sortingColumnName: string = Columns.Added,
    sortingDirection: SortingDirection = SortingDirection.Descending,
  ) => {
    const sortBy = columnToParamMap[sortingColumnName.toLowerCase() as Lowercase<Columns>];
    fetchAccountUsers(editAccountId, top, skip, sortBy, sortingDirection);
  };

  return (
    <>
      <TemplateUsers
        ref={ref}
        {...props}
        fetchUsers={loadAccountUsers}
        onCloseUsersFileUploadModal={() => setShowUsersFileUploadModal(false)}
        onIsDirtyChange={setIsDirty}
        showUsersFileUploadModal={showUserUploadModal}
      />
      <FormFooter
        isSaveBtnDisabled={!isDirty}
        isCancelBtnDisabled={!isDirty}
        onSave={ref.current?.save}
        onCancel={ref.current?.reset}
      />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { templateUsers } = state.accounts;
  const { isLoading, items, selected, itemsCount } = templateUsers;
  return {
    users: items,
    selectedIds: selected,
    isLoading,
    usersCount: itemsCount,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    onSelectedUsersChanged: bindAction(setSelectedUsers, dispatch),
    fetchAccountUsers: bindAction(fetchAccountUsers, dispatch),
    deleteUsers: bindAction(deleteUsers, dispatch),
  };
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditAccountUsersList);
export default ConnectedComponent;
